import { useAuth0 } from '@auth0/auth0-react';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useFilters, useGlobalFilter, useSortBy, useTable } from 'react-table';
import { useColumns } from '../hooks/useColumns';
import { Order, OrderSummary } from '../types';
import { groupOrders } from '../utils/utils';
import VirtualizedTable from './VirtualTable';

interface TableProps {
  orders: Order[];
  setMyOrdersSummary: React.Dispatch<React.SetStateAction<OrderSummary>>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      height: '69vh',
      [theme.breakpoints.down('md')]: {
        width: '100%',
        height: '69vh',
      },
    },
  })
);

const MyOrdersTable = React.forwardRef((props: TableProps, ref: any) => {
  const { user: userInfo }: any = useAuth0();
  const { columns } = useColumns();

  const { orders, setMyOrdersSummary } = props;

  const agentId: any = userInfo?.sub.replace(/.*\|/, '');

  const filterOrders = useCallback((orders: any, agentId: string) => {
    if (!orders || orders?.length === 0) return [];
    const filteredOrders = orders?.filter((o: any) => o.agentID === agentId);
    return filteredOrders;
  }, []);

  const allOrders = useMemo(() => filterOrders(orders, agentId), [filterOrders, orders, agentId]);
  const allColums = useMemo(() => columns, [columns]);

  const filterTypes = useMemo(
    () => ({
      multiple: (rows: any, id: any, filterValue: string) => {
        return rows.filter((row: any) => {
          const rowValue = row.values[id];
          return rowValue !== undefined ? filterValue.includes(rowValue) : true;
        });
      },
    }),
    []
  );

  const instance = useTable(
    {
      columns: allColums,
      data: allOrders,
      filterTypes,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: allColums.filter((col: any) => col.show === false).map((col: any) => col.accessor),
      },
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy
  );

  const { flatRows } = instance;

  // return table instance
  useImperativeHandle(ref, () => instance);

  const classes = useStyles();

  const history = useHistory();

  const viewOrderDetails = (
    orderID: string,
    externalOrderID: string,
    shopper: string,
    isCompleted: string,
    customerType: string,
    orderTypeID: string,
    shopName: string,
    startTime: string,
    shoppingCompleted: string,
    deliveryStarted: string,
    deliveryCompleted: string,
    agentID: string,
    driver: string,
    firstItemPickedTime: string | null
  ) => {
    history.push(`/orders/${orderID}`, {
      externalOrderID,
      shopper,
      isCompleted,
      customerType,
      orderTypeID,
      shopName,
      startTime,
      shoppingCompleted,
      deliveryStarted,
      deliveryCompleted,
      agentID,
      driver,
      firstItemPickedTime,
    });
  };

  useEffect(() => {
    const MyOrdersSummary = groupOrders(flatRows);
    setMyOrdersSummary(MyOrdersSummary);
  }, [flatRows, setMyOrdersSummary]);

  return (
    <Paper className={classes.root}>
      <VirtualizedTable instance={instance} onClickRow={viewOrderDetails} />
    </Paper>
  );
});

export default MyOrdersTable;
